/**
 * TeamChatManage — Coach's admin view for a single chat.
 * Members list with per-feature permission toggles, invite composer, and
 * remove/mute controls. Message feed lives here in M2.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, UserPlus, Trash2, Loader2, Mail, ShieldCheck, MessageSquare,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Layout } from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  chat: 'Team chat (always on)',
  own_stats: 'Their own player stats',
  fixtures: 'Upcoming fixtures',
  results: 'Match results',
  team_stats: 'Team stats & leaderboard',
};

export default function TeamChatManage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [chat, setChat] = useState(null);
  const [members, setMembers] = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteType, setInviteType] = useState('parent');
  const [sendingInvite, setSendingInvite] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/team-chat/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setChat(res.data);
      setMembers(res.data.members || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load chat');
      navigate('/team-chat');
    } finally {
      setLoading(false);
    }
  }, [chatId, navigate]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async () => {
    if (!inviteEmail.trim() || !inviteName.trim()) return;
    setSendingInvite(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/team-chat/${chatId}/invites`,
        { email: inviteEmail.trim(), display_name: inviteName.trim(), member_type: inviteType },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Invite email sent');
      setShowInvite(false);
      setInviteEmail('');
      setInviteName('');
      setInviteType('parent');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(false);
    }
  };

  const togglePermission = async (memberId, key, value) => {
    const optimistic = members.map((m) =>
      m.id === memberId ? { ...m, permissions: { ...(m.permissions || {}), [key]: value } } : m,
    );
    setMembers(optimistic);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/team-chat/${chatId}/members/${memberId}`,
        { permissions: { [key]: value } },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err) {
      toast.error('Failed to update permission');
      load();
    }
  };

  const removeMember = async (memberId, displayName) => {
    if (!window.confirm(`Remove ${displayName} from this chat?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/team-chat/${chatId}/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Member removed');
      load();
    } catch (err) {
      toast.error('Failed to remove member');
    }
  };

  if (loading) {
    return <Layout><div className="py-16 text-center"><Loader2 className="w-8 h-8 mx-auto text-gray-400 animate-spin" /></div></Layout>;
  }
  if (!chat) return null;

  const active = members.filter((m) => m.status === 'active');
  const invited = members.filter((m) => m.status === 'invited');

  return (
    <Layout>
    <div className="max-w-4xl mx-auto p-4 sm:p-6" data-testid="team-chat-manage-page">
      <Button
        variant="ghost"
        onClick={() => navigate('/team-chat')}
        className="mb-3 -ml-3"
        data-testid="back-to-chats-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-1.5" /> All chats
      </Button>

      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-black text-gray-900 truncate">{chat.title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {chat.season_name} · {active.length} member{active.length === 1 ? '' : 's'}
            {invited.length > 0 && <> · {invited.length} pending</>}
          </p>
        </div>
        <Button
          onClick={() => setShowInvite(true)}
          className="bg-[#007a33] hover:bg-[#005c26] text-white shrink-0"
          data-testid="invite-member-btn"
        >
          <UserPlus className="w-4 h-4 mr-1.5" /> Invite
        </Button>
      </div>

      <div className="mb-6 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900 flex items-start gap-2">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          Members can only see the features you enable per row below. Chat access is always on for active members.
          See our <a href="/docs/team-chat-safeguarding-policy.html" target="_blank" rel="noreferrer" className="underline">safeguarding policy</a>.
        </span>
      </div>

      {invited.length > 0 && (
        <section className="mb-6" data-testid="pending-invites-section">
          <h2 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Pending invites</h2>
          <ul className="space-y-2">
            {invited.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200"
                data-testid={`invited-member-${m.id}`}
              >
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{m.display_name}</p>
                  <p className="text-xs text-gray-500 truncate flex items-center gap-1">
                    <Mail className="w-3 h-3" /> {m.invite_email} · awaiting acceptance
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeMember(m.id, m.display_name)}
                  className="text-red-600 hover:text-red-700 shrink-0"
                  data-testid={`revoke-invite-${m.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section data-testid="active-members-section">
        <h2 className="text-xs font-black uppercase tracking-widest text-gray-500 mb-2">Active members</h2>
        {active.length === 0 ? (
          <div className="p-6 rounded-lg border border-dashed border-gray-300 text-center text-sm text-gray-500">
            No members yet. Send an invite to get started.
          </div>
        ) : (
          <ul className="space-y-3">
            {active.map((m) => {
              const perms = m.permissions || {};
              return (
                <li
                  key={m.id}
                  className="p-4 rounded-lg bg-white border border-gray-200"
                  data-testid={`active-member-${m.id}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{m.display_name}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {m.invite_email} · {m.member_type}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMember(m.id, m.display_name)}
                      className="text-red-600 hover:text-red-700 shrink-0"
                      data-testid={`remove-member-${m.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-3 border-t border-gray-100">
                    {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
                      <label
                        key={key}
                        className="flex items-center justify-between py-1.5 gap-2 text-xs"
                        data-testid={`permission-${m.id}-${key}`}
                      >
                        <span className={key === 'chat' ? 'text-gray-500' : 'text-gray-700'}>{label}</span>
                        <Switch
                          checked={perms[key] !== false}
                          onCheckedChange={(v) => togglePermission(m.id, key, v)}
                          disabled={key === 'chat'}
                        />
                      </label>
                    ))}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-[#007a33]" />
              Invite to {chat.title}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Display name</Label>
              <Input
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                placeholder="e.g. Sarah — Ella&apos;s mum"
                maxLength={80}
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label>Email address</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="parent@example.com"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Invitee type</Label>
              <div className="flex gap-2 mt-1">
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    checked={inviteType === 'parent'}
                    onChange={() => setInviteType('parent')}
                    data-testid="invite-type-parent"
                  />
                  Parent / guardian
                </label>
                <label className="flex items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    checked={inviteType === 'player'}
                    onChange={() => setInviteType('player')}
                    data-testid="invite-type-player"
                  />
                  Player (16+)
                </label>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                For players under 16, please invite the parent or guardian.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button
              onClick={sendInvite}
              disabled={!inviteEmail.trim() || !inviteName.trim() || sendingInvite}
              className="bg-[#007a33] hover:bg-[#005c26] text-white"
              data-testid="send-invite-btn"
            >
              {sendingInvite ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Sending…</> : 'Send invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
